import { Alert, Badge, Button, Group, Paper, Stack, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useAuth } from "../context/useAuth";
import { usersApi } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import type { User } from "../types/api";

function emailVerifyCopy(user: User): { prefix: string; alert?: string } {
  if (user.role === "company_user") {
    return {
      prefix: "to get email updates on submissions, hiring progress, and important account alerts.",
      alert: "Verify your email to receive submission and hiring updates.",
    };
  }
  if (user.role === "admin") {
    return {
      prefix: "to receive account and platform alerts.",
    };
  }
  if (user.role === "affiliate") {
    return {
      prefix: "to create and share your referral link.",
      alert: "Verify your email and phone before sharing your affiliate link.",
    };
  }
  return {
    prefix: "to submit candidates and receive updates.",
    alert: "Password accounts must verify email and phone before submitting candidates.",
  };
}

export function EmailVerificationCard() {
  const { user, setUser } = useAuth();
  const [code, setCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  if (!user) return null;

  if (user.auth_provider === "google") {
    return (
      <Paper withBorder p="lg">
        <Group justify="space-between" mb="sm">
          <Title order={4}>Email</Title>
          <Badge color="green" variant="light">
            Verified via Google
          </Badge>
        </Group>
        <Text fw="var(--axio-font-weight-medium)">{user.email}</Text>
      </Paper>
    );
  }

  const verified = !!user.email_verified_at;
  const copy = emailVerifyCopy(user);

  async function handleRequestOtp() {
    setSending(true);
    try {
      await usersApi.requestEmailOtp();
      setOtpSent(true);
      notifications.show({
        color: "green",
        title: "OTP sent",
        message: "Check your email for the verification code.",
      });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not send OTP";
      notifications.show({ color: "red", title: "Failed", message });
    } finally {
      setSending(false);
    }
  }

  async function handleVerify() {
    setVerifying(true);
    try {
      const updated = await usersApi.verifyEmailOtp(code);
      setUser(updated);
      setCode("");
      setOtpSent(false);
      notifications.show({
        color: "green",
        title: "Verified",
        message: "Your email is verified.",
      });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not verify OTP";
      notifications.show({ color: "red", title: "Failed", message });
    } finally {
      setVerifying(false);
    }
  }

  return (
    <Paper withBorder p="lg">
      <Group justify="space-between" mb="sm">
        <Title order={4}>Email verification</Title>
        {verified ? (
          <Badge color="green" variant="light">
            Verified
          </Badge>
        ) : (
          <Badge color="orange" variant="light">
            Not verified
          </Badge>
        )}
      </Group>
      <Text size="sm" c="dimmed" mb="md">
        Verify <strong>{user.email}</strong> {copy.prefix}
      </Text>
      {verified ? (
        <Text fw="var(--axio-font-weight-medium)">{user.email}</Text>
      ) : (
        <Stack>
          {copy.alert && <Alert color="yellow">{copy.alert}</Alert>}
          <Button color="violet" loading={sending} onClick={handleRequestOtp}>
            Send OTP to email
          </Button>
          {otpSent && (
            <>
              <TextInput
                label="OTP code"
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
              />
              <Button color="green" loading={verifying} onClick={handleVerify} disabled={code.trim().length < 4}>
                Verify email
              </Button>
            </>
          )}
        </Stack>
      )}
    </Paper>
  );
}
